import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';

/**
 * Getting Started (GettingStartedController). The API answers `enabled: false`, with nothing else, unless
 * GettingStarted:Enabled is on and either GettingStarted:Connections lists the connection or Tech Log has an
 * open onboarding plan for the facility with the wizard shown (GettingStartedGate), so the website shows
 * nothing. Auto setup (Tray Line Setup) and Check allergy protection follow the same answer.
 */

export type StepStatus = 'todo' | 'attention' | 'done' | 'skipped' | 'optional';

export interface GettingStartedCheck {
  ok: boolean;
  message: string;
}

export interface GettingStartedStep {
  key: string;
  sequence: number;
  title: string;
  status: StepStatus;
  /** What someone marked; null when not started. */
  marked: 'todo' | 'done' | 'skipped' | null;
  /** Why the step cannot be opened yet. */
  locked: string | null;
  canSkip: boolean;
  optional: boolean;
  check: GettingStartedCheck | null;
  answers: Record<string, unknown> | null;
  updatedBy: string | null;
  updatedUtc: string | null;
}

/** Counts only. Null = could not be read on this database. */
export interface GettingStartedFacts {
  mealTimesSet: boolean | null;
  units: number | null;
  rooms: number | null;
  unitsWithoutRooms: number | null;
  dietTypes: number | null;
  consistencies: number | null;
  /** Null when the database has no IDDSI column on textures. */
  texturesWithoutIddsi: number | null;
  residents: number | null;
  residentsWithoutDiet: number | null;
  printAgents: number | null;
  printAgentsOnline: number | null;
  /** Food allergies that protect nobody somewhere. Null = could not be checked, which blocks go-live. */
  unprotectedAllergies?: number | null;
  /** Blocking problems in the allergy check's steps 2 and 6; B4 is finished only at 0. */
  allergyBlockers?: number | null;
  /** Enabled Print Agent routing rules for tray tickets. */
  trayTicketPrinters?: number | null;
  activeMenuCycle: boolean | null;
  activeMenuName: string | null;
}

export interface GettingStartedState {
  /** On for this facility: the wizard, Auto setup and Check allergy protection. */
  enabled: boolean;
  /**
   * The user may use the wizard itself (Facility level 2 or PSI staff). False: only `enabled` is sent.
   * Absent from an API older than 2026-10-28, which refused such users instead.
   */
  canUse?: boolean;
  ready?: boolean;
  finished?: boolean;
  signOffBlocked?: string | null;
  steps?: GettingStartedStep[];
  facts?: GettingStartedFacts | null;
}

export const gettingStartedKeys = {
  all: ['gettingStarted'] as const,
  state: (facilityId: number) => [...gettingStartedKeys.all, facilityId] as const,
};

export function useGettingStarted(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: gettingStartedKeys.state(facilityId),
    queryFn: async () => (await apiClient.get<GettingStartedState>(API.gettingStarted(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    staleTime: 60 * 1000,
    // An older API slot has no route (404): that means "not here".
    retry: false,
  });
}

export function useSaveStepAnswers(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stepKey, answers }: { stepKey: string; answers: Record<string, unknown> }) =>
      (await apiClient.put<GettingStartedState>(API.gettingStartedAnswers(facilityId, stepKey), answers)).data,
    onSuccess: (state) => qc.setQueryData(gettingStartedKeys.state(facilityId), state),
  });
}

export function useSetStepStatus(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stepKey, status }: { stepKey: string; status: 'todo' | 'done' | 'skipped' }) =>
      (await apiClient.put<GettingStartedState>(API.gettingStartedStatus(facilityId, stepKey), { status })).data,
    onSuccess: (state) => qc.setQueryData(gettingStartedKeys.state(facilityId), state),
  });
}

/** Facility basics (B1): only the changed basics fields; the API refuses any other column. */
export function useSaveBasics(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: Record<string, unknown>) =>
      (await apiClient.put<GettingStartedState>(API.gettingStartedBasics(facilityId), fields)).data,
    onSuccess: (state) => qc.setQueryData(gettingStartedKeys.state(facilityId), state),
  });
}

/** Printing and reports (B9): only the changed print option fields; the API refuses any other column. */
export function useSavePrinting(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: Record<string, unknown>) =>
      (await apiClient.put<GettingStartedState>(API.gettingStartedPrinting(facilityId), fields)).data,
    onSuccess: (state) => qc.setQueryData(gettingStartedKeys.state(facilityId), state),
  });
}

/**
 * Printing and reports (B9): the printer for one report type, saved as a Print Agent routing rule
 * through the wizard (Facility level 2 or PSI staff). Only the agent, report type and printer go.
 */
export function useSaveWizardPrinter(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: { pKey_PrinterRouting: number; fKey_PrintAgent: number; reportType: string; printerName: string }) =>
      (await apiClient.put<GettingStartedState>(API.gettingStartedPrinter(facilityId), req)).data,
    onSuccess: (state) => {
      qc.setQueryData(gettingStartedKeys.state(facilityId), state);
      // printAgentApi's routing rules (['printAgent', 'routing', facilityId]).
      void qc.invalidateQueries({ queryKey: ['printAgent', 'routing', facilityId] });
    },
  });
}

/** Refresh status: recompute every step from the data and send them all to Tech Log. */
export function useSyncGettingStarted(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await apiClient.post<GettingStartedState>(API.gettingStartedSync(facilityId))).data,
    onSuccess: (state) => qc.setQueryData(gettingStartedKeys.state(facilityId), state),
  });
}

// ── Selective Touch (B10) ─────────────────────────────────────────────────

export interface TouchSettings {
  breakfastName: string | null;
  lunchName: string | null;
  dinnerName: string | null;
  /** "HH:mm" per meal (breakfast, lunch, dinner). */
  startTimes: (string | null)[];
  /** Facility-wide resident cut-offs per meal, "HH:mm", null where there is none. */
  cutoffs: (string | null)[];
  showConsistency: boolean;
  actAsPOS: boolean;
  useCredits: boolean;
  creditMeals: boolean[];
  creditsAutoRefill: boolean;
}

export interface TouchScreenCheck {
  mealtime: number;
  meal: string;
  screens: number;
  tabs: number;
  dietTabs: number;
  alwaysServed: boolean;
  tiles: number;
  problems: string[];
  missingCategories: string[];
  categoriesChecked: boolean;
  ok: boolean;
}

/** A staff login with access to the facility (names only; no PIN, no email). */
export interface TouchUser {
  userId: number;
  name: string;
  userName: string;
  touchLevel: number;
  creditLevel: number;
  pinSet: boolean;
  looksShared: boolean;
  ticketPrinters: number | null;
}

export interface TouchTablet {
  id: number;
  name: string;
  isActive: boolean;
  isEnrolled: boolean;
  lastSeen: string | null;
  secondsSinceSeen: number | null;
}

export interface TouchTestOrder {
  residentId: number | null;
  residentName: string | null;
  dietName: string | null;
  textureName: string | null;
  allergyName: string | null;
  createdUtc: string | null;
  orderLines: number;
  /** Meal charges for the test resident not voided yet. */
  unvoidedCharges: number;
  /** Meal charges the wizard voided before deleting it. */
  voidedCharges: number;
  allergenWarning: boolean;
  iddsiBadge: boolean | null;
  ticketPrinted: boolean;
  confirmedUtc: string | null;
  deletedUtc: string | null;
  deletedBy: string | null;
}

export interface TouchSetup {
  meals: number[];
  offeredMeals: boolean[];
  settings: TouchSettings;
  screens: TouchScreenCheck[];
  menuName: string | null;
  users: TouchUser[];
  usersUnreadable: boolean;
  tablets: TouchTablet[];
  printing: { ticketPrinters: number | null; printAgents: number | null; printAgentsOnline: number | null };
  testOrder: TouchTestOrder;
  /** The kiosk shows IDDSI badges on this site (#2595 and the column): the test must confirm one. */
  iddsiBadges: boolean;
  blockers: string[];
}

export const touchSetupKey = (facilityId: number) => [...gettingStartedKeys.all, 'touch', facilityId] as const;

export function useTouchSetup(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: touchSetupKey(facilityId),
    queryFn: async () => (await apiClient.get<TouchSetup>(API.gettingStartedTouch(facilityId))).data,
    enabled: enabled && facilityId > 0,
    staleTime: 30 * 1000,
    retry: false,
  });
}

/** A write that returns the wizard's state, then reads the touch setup again. */
function useTouchWrite<T>(facilityId: number, send: (body: T) => Promise<GettingStartedState>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: (state) => {
      qc.setQueryData(gettingStartedKeys.state(facilityId), state);
      return qc.invalidateQueries({ queryKey: touchSetupKey(facilityId) });
    },
  });
}

/** Show consistency / Act As POS; only those two columns, the API refuses any other. */
export function useSaveTouchSettings(facilityId: number) {
  return useTouchWrite(facilityId, async (fields: { selectiveMenuTouchShowConsistency?: boolean; selectiveMenuTouchActAsPOS?: boolean }) =>
    (await apiClient.put<GettingStartedState>(API.gettingStartedTouchSettings(facilityId), fields)).data);
}

export interface TestResidentRequest {
  unitId: number | null;
  dietTypeId: number;
  consistencyId: number;
  foodAllergyId: number;
}

export function useCreateTestResident(facilityId: number) {
  return useTouchWrite(facilityId, async (body: TestResidentRequest) =>
    (await apiClient.post<GettingStartedState>(API.gettingStartedTouchTestResident(facilityId), body)).data);
}

export function useConfirmTestOrder(facilityId: number) {
  return useTouchWrite(facilityId, async (body: { allergenWarning: boolean; iddsiBadge: boolean | null; ticketPrinted: boolean }) =>
    (await apiClient.post<GettingStartedState>(API.gettingStartedTouchTestConfirm(facilityId), body)).data);
}

export function useDeleteTestResident(facilityId: number) {
  return useTouchWrite(facilityId, async (_: void) =>
    (await apiClient.delete<GettingStartedState>(API.gettingStartedTouchTestResident(facilityId))).data);
}
